import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, X, Search } from 'lucide-react';

export default function MultiSelectDropdown({
  label,
  options = [],
  selected = [],
  onChange,
  placeholder = 'All',
  searchPlaceholder = 'Search...',
  maxDisplayTags = 2,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const getOptLabel = (opt) => (opt && typeof opt === 'object' ? opt.label : opt) ?? '';
  const getOptValue = (opt) => (opt && typeof opt === 'object' ? opt.value : opt) ?? '';

  const labelMap = useMemo(() => {
    const map = {};
    options.forEach((opt) => { map[getOptValue(opt)] = getOptLabel(opt); });
    return map;
  }, [options]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const lower = searchTerm.toLowerCase();
    return options.filter((opt) => getOptLabel(opt).toLowerCase().includes(lower));
  }, [options, searchTerm]);

  const toggleOption = (opt) => {
    const val = getOptValue(opt);
    if (selected.includes(val)) {
      onChange(selected.filter((s) => s !== val));
    } else {
      onChange([...selected, val]);
    }
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const removeTag = (e, val) => {
    e.stopPropagation();
    onChange(selected.filter((s) => s !== val));
  };

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      )}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-3 py-2 border rounded-lg cursor-pointer flex items-center justify-between min-h-[38px] bg-white ${
          isOpen
            ? 'border-blue-500 ring-2 ring-blue-500'
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <div className="flex flex-wrap gap-1 flex-1 min-w-0">
          {selected.length === 0 ? (
            <span className="text-gray-400 text-sm">{placeholder}</span>
          ) : selected.length <= maxDisplayTags ? (
            selected.map((s) => (
              <span
                key={s}
                className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium max-w-[140px]"
              >
                <span className="truncate">{labelMap[s] ?? s}</span>
                <X
                  className="w-3 h-3 shrink-0 cursor-pointer hover:text-blue-900"
                  onClick={(e) => removeTag(e, s)}
                />
              </span>
            ))
          ) : (
            <span className="text-sm text-gray-700">
              {selected.length} selected
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0 ml-1">
          {selected.length > 0 && (
            <X
              className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-pointer"
              onClick={clearAll}
            />
          )}
          <ChevronDown
            className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 flex flex-col">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
          {filteredOptions.length > 0 && (
            <div className="px-3 py-1.5 border-b border-gray-100 flex items-center justify-between">
              <label
                className="flex items-center gap-2 cursor-pointer text-sm text-gray-600"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={filteredOptions.length > 0 && filteredOptions.every((o) => selected.includes(getOptValue(o)))}
                  ref={(el) => {
                    if (el) {
                      const checkedCount = filteredOptions.filter((o) => selected.includes(getOptValue(o))).length;
                      el.indeterminate = checkedCount > 0 && checkedCount < filteredOptions.length;
                    }
                  }}
                  onChange={() => {
                    const filteredVals = filteredOptions.map(getOptValue);
                    const allSelected = filteredVals.every((v) => selected.includes(v));
                    if (allSelected) {
                      onChange(selected.filter((s) => !filteredVals.includes(s)));
                    } else {
                      const merged = new Set([...selected, ...filteredVals]);
                      onChange([...merged]);
                    }
                  }}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                />
                <span className="font-medium">Select all</span>
              </label>
              <span className="text-xs text-gray-400">{filteredOptions.length} items</span>
            </div>
          )}
          <div className="overflow-y-auto flex-1">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-400 text-center">
                No matches found
              </div>
            ) : (
              filteredOptions.map((opt) => {
                const val = getOptValue(opt);
                const lbl = getOptLabel(opt);
                return (
                  <label
                    key={val}
                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(val)}
                      onChange={() => toggleOption(opt)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                    />
                    <span className="truncate">{lbl}</span>
                  </label>
                );
              })
            )}
          </div>
          {selected.length > 0 && (
            <div className="p-2 border-t border-gray-100 flex justify-between items-center">
              <span className="text-xs text-gray-500">{selected.length} selected</span>
              <button
                type="button"
                onClick={clearAll}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
